import { Label, Typography, token } from "@may-i/mash-design-system";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import type { CameraWithAnalysisConfig } from "~/_legacy/types/analysis";

interface Props {
  cameras: CameraWithAnalysisConfig[];
  selectedFloorPlanId: number | null;
}

export function AddAdjustGuideCameraMemoList({ cameras, selectedFloorPlanId }: Props) {
  const { t } = useTranslation("shared");

  // 메모와 함께 소속 카메라의 analysisNumber 를 묶어서 노출 (인덱스가 아니라 카메라 번호 표기)
  const additionMemos = useMemo(() => {
    return cameras
      .filter(({ floorPlanId, additionConfig }) =>
        floorPlanId === selectedFloorPlanId && !!additionConfig?.additionId,
      )
      .flatMap((camera) =>
        camera.memos
          .filter(({ type }) => type === "adjustmentOrAddition")
          .map((memo) => ({ memo, analysisNumber: camera.analysisNumber })),
      );
  }, [cameras, selectedFloorPlanId]);

  const adjustmentMemos = useMemo(() => {
    return cameras
      .filter(({ floorPlanId, adjustmentConfig }) =>
        floorPlanId === selectedFloorPlanId && !!adjustmentConfig?.position,
      )
      .flatMap((camera) =>
        camera.memos
          .filter(({ type }) => type === "adjustmentOrAddition")
          .map((memo) => ({ memo, analysisNumber: camera.analysisNumber })),
      );
  }, [cameras, selectedFloorPlanId]);

  return (
    <Container>
      <Title>
        <Typography typo="body2" color="colors.text.secondary">
          {t("addAdjustGuideModal.addAdjustGuideCameraMemoList.title.textContent")}
        </Typography>
      </Title>
      <List>
        {additionMemos.map(({ memo, analysisNumber }) => (
          <Item key={memo.id}>
            <MemoLabel variant="soft" color="default" size="xSmall">
              {t("addAdjustGuideModal.addAdjustGuideCameraMemoList.memoTypeBadge.textContent.addition", {
                index: analysisNumber ?? "-",
              })}
            </MemoLabel>
            <Typography typo="caption" color="colors.gray.GR800">
              {memo.content}
            </Typography>
          </Item>
        ))}
        {adjustmentMemos.map(({ memo, analysisNumber }) => (
          <Item key={memo.id}>
            <MemoLabel variant="soft" color="default" size="xSmall">
              {t("addAdjustGuideModal.addAdjustGuideCameraMemoList.memoTypeBadge.textContent.adjustment", {
                index: analysisNumber ?? "-",
              })}
            </MemoLabel>
            <Typography typo="caption" color="colors.gray.GR800">
              {memo.content}
            </Typography>
          </Item>
        ))}
      </List>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  height: 100%;
`;

const Title = styled.div`
  display: flex;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  gap: 8px;
  border-radius: 8px;
  border: 1px solid ${token("colors.border.outline")};
  overflow-y: auto;
`;

const Item = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 12px 16px;
  border-bottom: 1px solid ${token("colors.border.outline")};
`;

const MemoLabel = styled(Label)`
  --label-color: #EB6227;
  --label-background-color: rgba(235, 98, 39, 0.16);
`;
